import { useState, useEffect, useContext } from 'react';
import { BrowserRouter as Router, Routes, Route, useNavigate } from 'react-router-dom';
import { CssVarsProvider } from '@mui/joy/styles';
import theme from './theme';
import Login from './pages/Login';
import NewPassword from './pages/NewPassword';
import Home from './pages/Home';
import Admin from './pages/Admin';
import InternalDocs from './pages/InternalDocs';
import ClientLayout from './client/ClientLayout';
import ClientProjects from './client/pages/ClientProjects';
import ClientMap from './client/pages/ClientMap';
import ClientLogin from './client/pages/ClientLogin';
import ClientNewPassword from './client/pages/ClientNewPassword';
import ClientIntDocs from './pages/ClientIntDocs';
import ClientHome from './client/pages/ClientHome';
import ProtectedClientRoute from './client/components/ProtectedClientRoute';
import MainLayout from './layouts/MainLayout';
import ClientIntDates from './pages/ClientIntDates';
import Staff from './pages/Staff';
import GISData from './pages/GISData';
import GISProjects from './pages/GISProjects';
import GISProjectMap from './pages/GISProjectMap';
import Permits from './pages/Permits';
import PermitDetail from './pages/PermitDetail';
import ProjectDetail from './pages/ProjectDetail';
import ProjectMap from './pages/ProjectMap';
import ClientDetails from './pages/ClientDetails';
import Clients from './pages/Clients';
import { AuthContext } from './context/AuthContext.jsx';
import GISMap from './client/pages/ClientMap';
import ClientProjectDetail from './client/pages/ClientProjectDetail';
import ClientProjectList from './client/pages/ClientProjectList';
import ClientPermitDetail from './client/pages/ClientPermitDetail';
import GPT from './pages/GPT';
import UserDetail from './pages/UserDetail';
import SubmittalTracker from './pages/SubmittalTracker';
import SurveyTracker from './pages/SurveyTracker';
import StatusBoard from './pages/StatusBoard';

const RootRedirect = ({ user }) => {
  const navigate = useNavigate();

  useEffect(() => {
    const host = window.location.hostname;

    if (host === 'clients.fldandp.com') {
      navigate('/client-login');
    } else if (host === 'thegrid.fldandp.com') {
      navigate(user ? '/home' : '/login');
    } else {
      navigate('/home');
    }
  }, [navigate, user]);

  return null;
};

const ProtectedRoute = ({ children }) => {
  const { user, loading } = useContext(AuthContext);
  const navigate = useNavigate();

  useEffect(() => {
    if (!loading && !user) {
      navigate('/login');
    }
  }, [user, loading, navigate]);

  if (loading) return null;
  return user ? children : null;
};

function App() {
  useEffect(() => {
    const hostname = window.location.hostname;

    if (hostname.includes('clients.fldandp.com')) {
      document.title = 'FLD&P Client Portal';
    } else if (hostname.includes('thegrid.fldandp.com')) {
      document.title = 'The Grid | FLD&P Intranet';
    } else {
      document.title = 'FLD&P';
    }
  }, []);

  return (
    <Router>
      <Routes>
        <Route path="/" element={<RootRedirect />} />
        <Route path="/client-login" element={<ClientLogin />} />
        <Route path="/client-new-password" element={<ClientNewPassword />} />
        <Route path="/login" element={<Login />} />
        <Route path="/new-password" element={<NewPassword />} />
        <Route element={<MainLayout />}>
          <Route path="/home" element={<ProtectedRoute><Home /></ProtectedRoute>} />
          <Route path="/admin" element={<ProtectedRoute><Admin /></ProtectedRoute>} />
          <Route path="/internal-docs" element={<ProtectedRoute><InternalDocs /></ProtectedRoute>} />
          <Route path="/client-int-docs" element={<ProtectedRoute><ClientIntDocs /></ProtectedRoute>} />
          <Route path="/client-int-dates" element={<ProtectedRoute><ClientIntDates /></ProtectedRoute>} />
          <Route path="/staff" element={<ProtectedRoute><Staff /></ProtectedRoute>} />
          <Route path="/GISData" element={<ProtectedRoute><GISData /></ProtectedRoute>} />
          <Route path="/gis-projects" element={<ProtectedRoute><GISProjects /></ProtectedRoute>} />
          <Route path="/gis-projects/:id" element={<ProtectedRoute><GISProjectMap /></ProtectedRoute>} />
          <Route path="/Permits" element={<ProtectedRoute><Permits /></ProtectedRoute>} />
          <Route path="/permits/:id" element={<ProtectedRoute><PermitDetail /></ProtectedRoute>} />
          <Route path="/projects/:id" element={<ProtectedRoute><ProjectDetail /></ProtectedRoute>} />
          <Route path="/map" element={<ProtectedRoute><ProjectMap /></ProtectedRoute>} />
          <Route path="/clients/:id" element={<ProtectedRoute><ClientDetails /></ProtectedRoute>} />
          <Route path="/clients" element={<ProtectedRoute><Clients /></ProtectedRoute>} />
          <Route path="/gpt" element={<ProtectedRoute><GPT /></ProtectedRoute>} />
          <Route path="/my-profile" element={<ProtectedRoute><UserDetail /></ProtectedRoute>} />
          <Route path="/submittals" element={<ProtectedRoute><SubmittalTracker /></ProtectedRoute>} />
          <Route path="/survey-tracker" element={<ProtectedRoute><SurveyTracker /></ProtectedRoute>} />
          <Route path="/status-board" element={<ProtectedRoute><StatusBoard /></ProtectedRoute>} />
        </Route>
        <Route path="/client" element={<ClientLayout />}>
          <Route path="home" element={<ProtectedClientRoute><ClientHome /></ProtectedClientRoute>} />
          <Route path="map" element={<ProtectedClientRoute><GISMap /></ProtectedClientRoute>} />
          <Route path="project" element={<ProtectedClientRoute><ClientProjectList /></ProtectedClientRoute>} />
          <Route path="projects/:id" element={<ProtectedClientRoute><ClientProjectDetail /></ProtectedClientRoute>} />
          <Route path="gis-map/:id" element={<ProtectedClientRoute><GISProjectMap /></ProtectedClientRoute>} />
          <Route path="permits/:id" element={<ProtectedClientRoute><ClientPermitDetail /></ProtectedClientRoute>} />
        </Route>
      </Routes>
    </Router>
  );
}

export default App;
